<template>
  <section class="dashboard-downloads" aria-label="Downloads">
    <marketing-tool-header
      eyebrow="Dashboard"
      title="Downloads"
      description="Deine verfügbaren Excel-Exporte."
      icon="mdi-file-download-outline"
      :logo-src="studioLogoUrl"
      :logo-alt="studioLogoAlt"
      compact-on-mobile></marketing-tool-header>

    <v-card v-if="availableDownloadCount" dark flat class="download-surface">
      <div class="surface-header">
        <h2>Verfügbar</h2>
        <span class="count-pill">{{ availableDownloadCount }} {{ availableDownloadCount === 1 ? "Export" : "Exporte" }}</span>
      </div>

      <div class="dashboard-downloads__grid">
        <article v-if="canDownloadGFHours" class="download-card">
          <div class="download-card__topline">
            <span class="download-card__icon" aria-hidden="true">
              <v-icon size="26">mdi-weight-lifter</v-icon>
            </span>
            <span class="download-card__format">XLSX</span>
          </div>

          <div class="download-card__body">
            <h2>Stundenabrechnung Group Fitness</h2>
          </div>

          <v-btn class="download-card__action" color="#009640" depressed large :disabled="Boolean(activeDownload)" @click="selectingGFMonth = true">
            <v-icon left size="20">mdi-calendar-month-outline</v-icon>
            Monat wählen
          </v-btn>
        </article>

        <article v-if="canDownloadAlibey" class="download-card">
          <div class="download-card__topline">
            <span class="download-card__icon" aria-hidden="true">
              <v-icon size="26">mdi-palm-tree</v-icon>
            </span>
            <span class="download-card__format">XLSX</span>
          </div>

          <div class="download-card__body">
            <h2>Alibey Buchungen</h2>
          </div>

          <v-btn
            class="download-card__action"
            color="#009640"
            depressed
            large
            :loading="activeDownload === 'alibey'"
            :disabled="Boolean(activeDownload)"
            @click="downloadAlibeyBookings">
            <v-icon left size="20">mdi-download</v-icon>
            Herunterladen
          </v-btn>
        </article>
      </div>
    </v-card>

    <div v-else class="dashboard-downloads__empty" role="status">
      <span class="dashboard-downloads__empty-icon" aria-hidden="true">
        <v-icon size="30">mdi-folder-lock-outline</v-icon>
      </span>
      <div>
        <h2>Keine Downloads verfügbar</h2>
        <p>Für dein Benutzerkonto sind aktuell keine Exporte freigeschaltet.</p>
      </div>
    </div>

    <v-dialog v-model="selectingGFMonth" persistent max-width="430" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="download-dialog" dark>
        <v-card-title class="download-dialog__title">
          <span class="download-dialog__title-icon" aria-hidden="true">
            <v-icon size="22">mdi-calendar-month-outline</v-icon>
          </span>
          <span>Abrechnungsmonat</span>
        </v-card-title>

        <v-card-text class="download-dialog__content">
          <v-date-picker
            v-model="selectedGFMonth"
            class="download-dialog__picker"
            color="#009640"
            locale="de-CH"
            min="2022-05"
            :max="lastMonth"
            type="month"
            full-width
            no-title></v-date-picker>
        </v-card-text>

        <v-card-actions class="download-dialog__actions">
          <v-btn text large :disabled="activeDownload === 'groupFitness'" @click="selectingGFMonth = false">Abbrechen</v-btn>
          <v-btn
            color="#009640"
            depressed
            large
            :loading="activeDownload === 'groupFitness'"
            :disabled="!selectedGFMonth || Boolean(activeDownload)"
            @click="downloadGFHours">
            <v-icon left size="20">mdi-download</v-icon>
            Herunterladen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import { saveAs } from "file-saver"
import writeXlsxFile from "write-excel-file"
import api from "../../../api"
import userService from "../../../services/userService"
import MarketingToolHeader from "../communication/MarketingToolHeader.vue"

export default {
  name: "DashboardDownloads",
  components: { MarketingToolHeader },
  props: {
    studioLogoUrl: {
      type: String,
      default: "",
    },
    studioLogoAlt: {
      type: String,
      default: "Studio Logo",
    },
  },
  data() {
    return {
      userService,
      activeDownload: null,
      selectingGFMonth: false,
      selectedGFMonth: null,
    }
  },
  computed: {
    canDownloadAlibey() {
      return this.userService.hasRight("af_downloadList")
    },
    canDownloadGFHours() {
      return this.userService.hasRight("gf_s_view") && this.isWellComeFitHost
    },
    availableDownloadCount() {
      return Number(this.canDownloadGFHours) + Number(this.canDownloadAlibey)
    },
    isWellComeFitHost() {
      if (typeof window === "undefined") return false

      const host = window.location.host.toLowerCase()
      return ["dev", "192", "local", "wellcomefit", "wintifit"].some((part) => host.includes(part))
    },
    lastMonth() {
      return this.$moment().startOf("month").subtract(1, "month").format("YYYY-MM")
    },
  },
  created() {
    this.selectedGFMonth = this.lastMonth
  },
  methods: {
    async downloadGFHours() {
      if (!this.selectedGFMonth || this.activeDownload) return

      this.activeDownload = "groupFitness"

      try {
        const selectedMonth = this.$moment(this.selectedGFMonth, "YYYY-MM")
        const monthOffset = selectedMonth.diff(this.$moment(), "months") * -1
        const blob = await api.getGFHours(monthOffset)
        const fileName = `StundenGF_${selectedMonth.format("YYYY-MM-DD")}`

        await this.saveDownload(blob, fileName)
        this.selectingGFMonth = false
        this.$toast.success("Stundenabrechnung wurde erstellt")
      } catch (error) {
        this.$toast.error("Stundenabrechnung konnte nicht erstellt werden")
      } finally {
        this.activeDownload = null
      }
    },
    async downloadAlibeyBookings() {
      if (this.activeDownload) return

      this.activeDownload = "alibey"

      try {
        const entries = await api.downloadListCategory("alibey")
        const rows = [
          [
            { value: "Datum", type: String, fontWeight: "bold" },
            { value: "Studio", type: String, fontWeight: "bold", align: "center" },
            { value: "An- und Abreisedatum", type: String, fontWeight: "bold", align: "center" },
            { value: "Übernachtungen", type: String, fontWeight: "bold", align: "center" },
            { value: "Raum", type: String, fontWeight: "bold", align: "center" },
            { value: "Preis pro Tag", type: String, fontWeight: "bold", align: "center" },
            { value: "Gesamtpreis", type: String, fontWeight: "bold", align: "center" },
            { value: "Empfohlen durch", type: String, fontWeight: "bold", align: "center" },
            { value: "Personen", type: String, fontWeight: "bold", align: "center" },
          ],
        ]

        entries.forEach((entry) => {
          const roomSubtitle = entry.content.room.subtitle || ""
          const row = [
            { value: new Date(entry.created * 1000).toLocaleDateString("de-CH"), type: String },
            { value: entry.createdBy.studio_id, type: String },
            { value: `${entry.content.dateRange[0]} bis ${entry.content.dateRange[1]}`, type: String },
            { value: entry.content.duration.toString(), type: String },
            { value: `${entry.content.room.title} (${roomSubtitle})`, type: String },
            { value: entry.content.room.possibility.price.toString(), type: String },
            {
              value: Math.round(entry.content.room.possibility.price * entry.content.duration + 119 * entry.content.adults).toString(),
              type: String,
            },
            { value: entry.content.form.recommendedBy ? entry.content.form.recommendedBy.toString() : "", type: String },
          ]

          entry.content.form.persons.forEach((person) => {
            row.push({ value: this.formatPerson(person), type: String })
          })

          rows.push(row)
        })

        const blob = await writeXlsxFile(rows)
        await this.saveDownload(blob, "AlibeyBuchungen")
        this.$toast.success("Alibey Buchungen wurden erstellt")
      } catch (error) {
        this.$toast.error("Alibey Buchungen konnten nicht erstellt werden")
      } finally {
        this.activeDownload = null
      }
    },
    formatPerson(person) {
      const email = person.email ? ` ${person.email}` : ""
      return `${person.firstName} ${person.lastName} ${person.birthDayPicker.date} Tshirt: ${person.shirtSize}${email} | `
    },
    saveDownload(blob, fileName) {
      if (typeof window === "undefined" || !window.flutter_inappwebview) {
        saveAs(blob, `${fileName}.xlsx`)
        return Promise.resolve()
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = reject
        reader.onloadend = () => {
          const dataUrl = reader.result || ""
          const base64 = dataUrl.split(",")[1]

          if (!base64) {
            reject(new Error("Download konnte nicht für die App vorbereitet werden"))
            return
          }

          try {
            Promise.resolve(window.flutter_inappwebview.callHandler("blobToBase64Handler", base64, "xlsx", fileName)).then(resolve).catch(reject)
          } catch (error) {
            reject(error)
          }
        }
        reader.readAsDataURL(blob)
      })
    },
  },
}
</script>

<style scoped>
.dashboard-downloads {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.download-surface {
  margin-top: 1rem;
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.surface-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1.125rem;
}

.surface-header h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.35;
}

.count-pill,
.download-card__format {
  display: inline-flex;
  min-height: 1.75rem;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.25rem;
  white-space: nowrap;
}

.dashboard-downloads__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.875rem;
}

.download-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 13rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
  transition: border-color 180ms ease, background-color 180ms ease, transform 180ms ease;
}

.download-card__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.download-card__icon,
.dashboard-downloads__empty-icon,
.download-dialog__title-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.download-card__icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
}

.download-card__icon .v-icon,
.dashboard-downloads__empty-icon .v-icon,
.download-dialog__title-icon .v-icon {
  color: currentColor;
}

.download-card__body {
  flex: 1 1 auto;
  padding: 1.25rem 0;
}

.download-card__body h2,
.dashboard-downloads__empty h2 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  font-weight: 750;
  line-height: 1.4;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.download-card__action {
  align-self: flex-start;
  min-height: 2.75rem !important;
  padding: 0 1rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0 !important;
  text-transform: none;
}

.dashboard-downloads__empty {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 8rem;
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px dashed var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
}

.dashboard-downloads__empty-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.8125rem;
}

.dashboard-downloads__empty p {
  margin: 0.5rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.download-dialog {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  color: var(--text);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
}

.download-dialog__title {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem !important;
  border-bottom: 1px solid var(--border);
  color: var(--text);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
  font-size: 1.25rem !important;
  font-weight: 800 !important;
  line-height: 1.35;
}

.download-dialog__title-icon {
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.8125rem;
  background: rgba(0, 150, 64, 0.12);
}

.download-dialog__content {
  padding: 1.25rem !important;
}

.download-dialog__picker {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 0.8125rem;
}

.download-dialog__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.download-dialog__actions .v-btn {
  min-height: 2.75rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .download-card:hover {
    border-color: var(--border-strong);
    background: var(--surface-strong);
    transform: translateY(-2px);
  }
}

@media (max-width: 78.9375rem) {
  .dashboard-downloads {
    padding: 1.125rem;
  }
}

@media (max-width: 47.5rem) {
  .dashboard-downloads__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .download-card {
    min-height: 11.5rem;
  }
}

@media (max-width: 37.4375rem) {
  .dashboard-downloads {
    padding: 0;
  }

  .download-surface {
    margin-top: 0.625rem;
    padding: 0.75rem;
    border-radius: 1rem !important;
  }

  .surface-header {
    align-items: center;
    flex-direction: row;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .surface-header h2 {
    font-size: 1rem;
  }

  .dashboard-downloads__grid {
    gap: 0.5rem;
  }

  .download-card {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 0.625rem;
    min-height: 0;
    padding: 0.75rem;
    border-radius: 0.875rem;
  }

  .download-card__topline {
    display: block;
    grid-column: 1;
    grid-row: 1;
  }

  .download-card__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.75rem;
  }

  .download-card__format {
    display: none;
  }

  .download-card__body {
    grid-column: 2;
    grid-row: 1;
    padding: 0;
  }

  .download-card__action {
    grid-column: 1 / -1;
    width: 100%;
    margin-top: 0.125rem;
  }

  .dashboard-downloads__empty {
    min-height: 0;
    margin-top: 0.625rem;
    padding: 0.875rem;
  }

  .dashboard-downloads__empty-icon {
    width: 2.5rem;
    height: 2.5rem;
  }

  .dashboard-downloads__empty p {
    display: none;
  }

  .download-dialog {
    border: 0;
    border-radius: 0 !important;
  }

  .download-dialog__actions {
    flex-direction: column-reverse;
    padding: 0.75rem 0.875rem max(0.875rem, env(safe-area-inset-bottom, 0px)) !important;
  }

  .download-dialog__actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }
}

@media (max-width: 22.4375rem) {
  .download-surface {
    padding: 0.625rem;
  }

  .download-card {
    gap: 0.5rem;
    padding: 0.625rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .download-card {
    transition-duration: 0.01ms !important;
  }
}
</style>
